import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  IdentificationCard,
  ShieldCheck,
  Users,
  ClockCounterClockwise,
  UserMinus,
  UserPlus,
  ArrowsLeftRight,
  PaperPlaneTilt,
  LinkSimple,
} from "@phosphor-icons/react";
import LoaderLoop from "../shared/loader/LoaderLoop";
import ModalCloseButton from "../ui/ModalCloseButton";
import {
  selectClinicId,
  selectClinicProfile,
  selectTherapistProfile,
  selectTherapistId,
  selectTherapistName,
} from "../../store/slices/authSlice";
import {
  therapistApi,
  useUpdateTherapistRoleMutation,
  useSetTherapistStatusMutation,
  useResendTeamInviteMutation,
} from "../../store/services/therapistApi";
import { useGetPatientsQuery } from "../../store/services/patientsApi";
import { useGetActivityLogsQuery, ACTION_LABELS } from "../../store/services/activityLogsApi";
import { useToast } from "../../contexts/ToastContext";
import usePlanPermissions from "../../hooks/usePlanPermissions";
import { deriveClinicRole, CLINIC_ROLE_LABELS } from "../../config/permissionsRegistry";
import ConfirmDialog from "../ui/ConfirmDialog";
import RolePermissionsTab from "./RolePermissionsTab";
import DeactivateMemberModal from "./DeactivateMemberModal";
import TransferPatientsModal from "./TransferPatientsModal";
import { memberName, memberInitials, lastLoginText, roleBadge } from "./teamUtils";
import ContentLoader from "../shared/ContentLoader";

const LOGIN_URL = "https://clinikit.co.il/login";

const TABS = [
  { id: "details", label: "פרטים", icon: IdentificationCard },
  { id: "role", label: "תפקיד והרשאות", icon: ShieldCheck },
  { id: "patients", label: "מטופלים", icon: Users },
  { id: "activity", label: "פעילות", icon: ClockCounterClockwise },
];

const fmtDate = (v) => {
  if (!v) return "—";
  const d = v?.toDate ? v.toDate() : new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" });
};

/**
 * Team member popup — pat-wiz style tabs: פרטים | תפקיד והרשאות | מטופלים | פעילות.
 */
/** ms epoch of the member's permissionsUpdatedAt (ISO after serializeForRedux) or null. */
const permissionsStampOf = (t) => {
  const raw = t?.permissionsUpdatedAt;
  if (!raw) return null;
  const d = raw?.toDate ? raw.toDate() : new Date(raw);
  const ms = d.getTime();
  return Number.isNaN(ms) ? null : ms;
};

function TeamMemberPopup({ therapist, onClose }) {
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const clinic = useSelector(selectClinicProfile);
  const me = useSelector(selectTherapistProfile);
  const myId = useSelector(selectTherapistId);
  const myName = useSelector(selectTherapistName);
  const { showSuccess, showError } = useToast();
  const { checkTherapist, PlanLimitModalComponent } = usePlanPermissions();
  const [updateRole, { isLoading: isSaving }] = useUpdateTherapistRoleMutation();
  const [setStatus, { isLoading: isReactivating }] = useSetTherapistStatusMutation();
  const [resendInvite, { isLoading: isResending }] = useResendTeamInviteMutation();
  const isInvitePending = therapist?.inviteStatus === "pending";

  const targetId = therapist?.uid || therapist?.id;
  const isSelf = targetId === myId;
  const actorIsOwner = me?.role === "owner";
  const targetRole = deriveClinicRole(therapist);
  const isInactive = therapist?.isActive === false;
  const isSecretary = targetRole === "secretary";
  // Actor matrix: only the owner may deactivate / reactivate a manager
  // (reactivation restores the manager's full admin access as-is).
  const statusLockedForManager = targetRole === "manager" && !actorIsOwner;
  const [confirmReactivate, setConfirmReactivate] = useState(false);

  const handleResendInvite = async () => {
    try {
      const res = await resendInvite({ clinicId, therapistId: targetId }).unwrap();
      showSuccess(`תזכורת נשלחה (${res?.inviteSmsCount ?? ""}/5)`);
    } catch (err) {
      console.error("[CK-5548] Error resending invite:", {
        errorCode: "CK-5548",
        errorMessage: err?.message,
        functionCode: err?.code,
        clinicId,
        targetTherapistId: targetId,
        timestamp: new Date().toISOString(),
      });
      showError(err?.message && /[\u0590-\u05FF]/.test(err.message) ? err.message : "שליחת התזכורת נכשלה. נסו שוב.");
    }
  };

  const handleCopyLoginLink = async () => {
    try {
      await navigator.clipboard.writeText(LOGIN_URL);
      showSuccess("קישור הכניסה הועתק");
    } catch {
      showError("לא ניתן להעתיק. הקישור: " + LOGIN_URL);
    }
  };


  const [tab, setTab] = useState("details");
  const [draft, setDraft] = useState(null);
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [transfer, setTransfer] = useState(null); // { patientIds: [] | null }
  const [selectedPatients, setSelectedPatients] = useState(() => new Set());

  // Role tab read-only conditions
  const roleReadOnly = useMemo(() => {
    if (isSelf) return { ro: true, why: "לא ניתן לשנות את התפקיד של עצמך." };
    if (targetRole === "owner") return { ro: true, why: "לא ניתן לשנות את תפקיד הבעלים." };
    if (targetRole === "manager" && !actorIsOwner)
      return { ro: true, why: "ניתן לשינוי על ידי בעל/ת הקליניקה בלבד." };
    if (isInactive) return { ro: true, why: "יש להפעיל את המטפל/ת כדי לערוך הרשאות." };
    return { ro: false, why: null };
  }, [isSelf, targetRole, actorIsOwner, isInactive]);

  // Serializable snapshot of a draft (role + permissions + explicit overrides).
  const snapshotOf = (d) =>
    JSON.stringify({
      r: d.clinicRole,
      p: d.permissions,
      o: Array.from(d.overridden instanceof Set ? d.overridden : []).sort(),
    });

  const effectiveForCompare = useMemo(() => {
    if (!draft) return null;
    return snapshotOf(draft);
  }, [draft]);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const dirty = draft && effectiveForCompare !== (savedSnapshot ?? null) && savedSnapshot !== undefined
    ? savedSnapshot === null
      ? false
      : effectiveForCompare !== savedSnapshot
    : false;

  // First draft arrival = baseline
  const handleDraftChange = (next) => {
    setDraft(next);
    if (savedSnapshot === null) setSavedSnapshot(snapshotOf(next));
  };

  // Stale-draft recovery: after the server refuses a save because another
  // admin changed this member (functions/aborted), refetch the team list and
  // rebuild the draft from the fresh doc once its stamp actually changes.
  const [staleStamp, setStaleStamp] = useState(undefined); // undefined = not pending
  const currentStamp = permissionsStampOf(therapist);
  useEffect(() => {
    if (staleStamp === undefined) return;
    if (currentStamp === staleStamp) return;
    setDraft(null);
    setSavedSnapshot(null);
    setStaleStamp(undefined);
  }, [staleStamp, currentStamp]);

  const handleSave = async () => {
    if (!draft || roleReadOnly.ro) return;
    try {
      await updateRole({
        clinicId,
        therapistId: targetId,
        clinicRole: draft.clinicRole,
        permissions: draft.permissions,
        overriddenKeys: Array.from(draft.overridden instanceof Set ? draft.overridden : []),
        expectedPermissionsUpdatedAt: currentStamp,
      }).unwrap();
      setSavedSnapshot(snapshotOf(draft));
      showSuccess(`ההרשאות של ${memberName(therapist)} עודכנו. השינוי נכנס לתוקף מיידית.`);
    } catch (err) {
      console.error("[CK-5544] Error saving role/permissions:", {
        errorCode: "CK-5544",
        errorMessage: err?.message,
        functionCode: err?.code,
        clinicId,
        targetTherapistId: targetId,
        timestamp: new Date().toISOString(),
      });
      if (err?.code === "functions/aborted") {
        setStaleStamp(currentStamp);
        dispatch(therapistApi.util.invalidateTags([{ type: "Therapist", id: "CLINIC_LIST" }]));
      }
      showError(err?.message && /[֐-׿]/.test(err.message) ? err.message : "שגיאה בשמירת ההרשאות. נסו שוב.");
    }
  };

  const handleReactivate = async () => {
    setConfirmReactivate(false);
    // Reactivation re-occupies a plan seat — same gate as adding a member.
    const canProceed = await checkTherapist();
    if (!canProceed) return;
    try {
      await setStatus({
        clinicId,
        therapistId: targetId,
        isActive: true,
        actorId: myId,
        actorName: myName,
        targetName: memberName(therapist),
      }).unwrap();
      showSuccess("איש/אשת הצוות הופעל/ה מחדש");
      onClose();
    } catch (err) {
      console.error("[CK-5545] Error reactivating team member:", {
        errorCode: "CK-5545",
        errorMessage: err?.message,
        clinicId,
        targetTherapistId: targetId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בהפעלה מחדש. נסו שוב.");
    }
  };

  // ----- patients tab data (primary + shared) -----
  const { data: patientsData, isLoading: loadingPatients } = useGetPatientsQuery(
    { clinicId, therapistId: targetId, pageSize: 200 },
    { skip: !clinicId || !targetId || tab !== "patients" || isSecretary },
  );
  const patients = patientsData?.patients || [];

  // ----- activity tab data -----
  const { data: activityData, isLoading: loadingActivity } = useGetActivityLogsQuery(
    { clinicId, therapistId: targetId, pageSize: 20 },
    { skip: !clinicId || !targetId || tab !== "activity" },
  );
  const logs = activityData?.logs || [];

  const togglePatient = (id) =>
    setSelectedPatients((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="tm-overlay" onClick={onClose}>
      <div className="tm-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="tm-modal-header">
          <div className="tm-modal-title">
            <span className="tm-modal-title-icon" aria-hidden>
              {therapist?.profileImage ? (
                <img src={therapist.profileImage} alt="" style={{ width: 38, height: 38, borderRadius: "var(--radius-md)", objectFit: "cover" }} />
              ) : (
                memberInitials(therapist)
              )}
            </span>
            <div className="tm-modal-title-text">
              <span className="tm-eyebrow">
                {roleBadge(therapist)}
                {isInactive ? " · לא פעיל/ה" : ""}
                {isSelf ? " · את/ה" : ""}
              </span>
              <h3>{memberName(therapist)}</h3>
            </div>
          </div>
          <div className="tm-modal-header-actions">
            <ModalCloseButton onClick={onClose} />
          </div>
        </div>

        <div className="tm-tabs" role="tablist">
          {TABS.filter((t) => !(t.id === "patients" && isSecretary)).map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`tm-tab ${active ? "active" : ""}`}
                onClick={() => setTab(t.id)}
              >
                <Icon size={15} weight={active ? "fill" : "regular"} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>

        <div className="tm-body">
          {tab === "details" && (
            <>
              <dl className="tm-kv">
                <dt>תפקיד</dt>
                <dd>{CLINIC_ROLE_LABELS[targetRole]}</dd>
                <dt>סטטוס</dt>
                <dd>{isInactive ? "לא פעיל/ה" : "פעיל/ה"}</dd>
                <dt>אימייל</dt>
                <dd dir="ltr" style={{ textAlign: "end" }}>{therapist?.email || "—"}</dd>
                <dt>טלפון</dt>
                <dd dir="ltr" style={{ textAlign: "end" }}>{therapist?.phone || "—"}</dd>
                <dt>התמחות</dt>
                <dd>{therapist?.specialty || therapist?.subSpecialization || "—"}</dd>
                <dt>כניסה אחרונה</dt>
                <dd>{lastLoginText(therapist)}</dd>
                <dt>הצטרפות</dt>
                <dd>{fmtDate(therapist?.registrationDate || therapist?.createdAt)}</dd>
                {therapist?.permissionsUpdatedAt && (
                  <>
                    <dt>הרשאות עודכנו</dt>
                    <dd>{fmtDate(therapist.permissionsUpdatedAt)}</dd>
                  </>
                )}
              </dl>
              {isSecretary && <span className="tm-caption">ללא יומן ומטופלים אישיים.</span>}
              {isInvitePending && !isInactive && (
                <div className="tm-locked-toggle" style={{ flexWrap: "wrap" }}>
                  <div className="tm-perm-text">
                    <span className="tm-perm-title">
                      <span className="tm-chip warning">ממתין/ה לכניסה ראשונה</span>
                    </span>
                    <span className="tm-perm-desc">
                      {therapist?.inviteSmsCount > 0
                        ? `נשלחו ${therapist.inviteSmsCount} הודעות SMS עם קישור לכניסה.`
                        : "טרם נשלחה הודעת SMS."}
                      {therapist?.inviteSmsError ? " השליחה האחרונה נכשלה." : ""}
                      {" "}הכניסה היא עם מספר הטלפון וקוד SMS.
                    </span>
                  </div>
                  <div className="tm-meta">
                    <button type="button" className="tm-btn secondary" style={{ minHeight: 34, padding: "0 10px" }} onClick={handleCopyLoginLink}>
                      <LinkSimple size={14} weight="bold" />
                      העתקת קישור כניסה
                    </button>
                    <button type="button" className="tm-btn primary" style={{ minHeight: 34, padding: "0 10px" }} onClick={handleResendInvite} disabled={isResending}>
                      {isResending ? <LoaderLoop size="xs" /> : <PaperPlaneTilt size={14} weight="bold" />}
                      שליחת תזכורת
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {tab === "role" && (
            <RolePermissionsTab
              therapist={therapist}
              clinic={clinic}
              actorIsOwner={actorIsOwner}
              readOnly={roleReadOnly.ro}
              readOnlyReason={roleReadOnly.why}
              draft={draft}
              onDraftChange={handleDraftChange}
            />
          )}

          {tab === "patients" && (
            <>
              {loadingPatients ? (
                <ContentLoader size="sm" message="טוען מטופלים…" />
              ) : patients.length === 0 ? (
                <div className="tm-empty">אין מטופלים משויכים</div>
              ) : (
                <>
                  <div className="tm-bulkbar">
                    <span>
                      {selectedPatients.size > 0 ? `${selectedPatients.size} נבחרו` : `${patients.length} מטופלים`}
                    </span>
                    <div className="tm-meta">
                      <button
                        type="button"
                        className="tm-btn ghost"
                        style={{ minHeight: 30, padding: "0 8px" }}
                        onClick={() =>
                          setSelectedPatients(
                            selectedPatients.size === patients.length ? new Set() : new Set(patients.map((p) => p.id)),
                          )
                        }
                      >
                        {selectedPatients.size === patients.length ? "ניקוי בחירה" : "בחירת הכל"}
                      </button>
                      <button
                        type="button"
                        className="tm-btn secondary"
                        style={{ minHeight: 30, padding: "0 10px" }}
                        onClick={() =>
                          setTransfer({ patientIds: selectedPatients.size > 0 ? Array.from(selectedPatients) : null })
                        }
                      >
                        <ArrowsLeftRight size={14} weight="bold" />
                        {selectedPatients.size > 0 ? "העברת הנבחרים" : "העברת כולם"}
                      </button>
                    </div>
                  </div>
                  <ul className="tm-list">
                    {patients.map((p) => {
                      const primary = p.therapistId === targetId;
                      return (
                        <li key={p.id} className="tm-list-row">
                          <label>
                            <input
                              type="checkbox"
                              checked={selectedPatients.has(p.id)}
                              onChange={() => togglePatient(p.id)}
                            />
                            <span className="tm-list-main">
                              <strong>
                                {p.firstName} {p.lastName}
                              </strong>
                              <span className="tm-list-sub">{primary ? "מטפל/ת ראשי/ת" : "מטופל/ת משותף/ת"}</span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </>
          )}

          {tab === "activity" && (
            <>
              {loadingActivity ? (
                <ContentLoader size="sm" message="טוען פעילות…" />
              ) : logs.length === 0 ? (
                <div className="tm-empty">אין פעילות מתועדת</div>
              ) : (
                <ul className="tm-list">
                  {logs.map((log) => (
                    <li key={log.id} className="tm-list-row">
                      <span className="tm-list-main">
                        <strong>{log.actionLabel || ACTION_LABELS[log.action] || log.action}</strong>
                        <span className="tm-list-sub">
                          {log.entityName ? `${log.entityName} · ` : ""}
                          {fmtDate(log.createdAt)}
                          {log.success === false ? " · נכשל" : ""}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        <div className="tm-footer">
          {tab === "details" && !isSelf && targetRole !== "owner" && (
            isInactive ? (
              <button
                type="button"
                className="tm-btn secondary"
                onClick={() => setConfirmReactivate(true)}
                disabled={isReactivating || statusLockedForManager}
                title={statusLockedForManager ? "רק בעל/ת הקליניקה יכול/ה להפעיל מחדש מנהל/ת" : undefined}
              >
                {isReactivating ? <LoaderLoop size="xs" /> : <UserPlus size={16} weight="bold" />}
                הפעלה מחדש
              </button>
            ) : (
              <button
                type="button"
                className="tm-btn secondary"
                onClick={() => setShowDeactivate(true)}
                disabled={statusLockedForManager}
                title={statusLockedForManager ? "רק בעל/ת הקליניקה יכול/ה להשבית מנהל/ת" : undefined}
              >
                <UserMinus size={16} weight="bold" />
                השבתה
              </button>
            )
          )}
          {tab === "role" && !roleReadOnly.ro && (
            <>
              <span className="tm-footer-note">{dirty ? "יש שינויים שלא נשמרו" : "הכל שמור"}</span>
              <button type="button" className="tm-btn primary" onClick={handleSave} disabled={!dirty || isSaving}>
                {isSaving ? <LoaderLoop size="xs" /> : <ShieldCheck size={16} weight="bold" />}
                {isSaving ? "שומר..." : "שמירת הרשאות"}
              </button>
            </>
          )}
          {(tab === "patients" || tab === "activity" || (tab === "role" && roleReadOnly.ro)) && (
            <button type="button" className="tm-btn secondary" onClick={onClose}>
              סגירה
            </button>
          )}
        </div>
      </div>

      <PlanLimitModalComponent />
      {confirmReactivate && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmDialog
            isOpen
            type="warning"
            title={`הפעלה מחדש של ${memberName(therapist)}`}
            message={
              targetRole === "manager"
                ? "החשבון יופעל מחדש כמנהל/ת עם גישת ניהול מלאה (כפי שהיה לפני ההשבתה). לצמצום ההרשאות יש לשנות תפקיד לאחר ההפעלה."
                : `החשבון יופעל מחדש בתפקיד ${CLINIC_ROLE_LABELS[targetRole] || ""} עם ההרשאות שהיו לו לפני ההשבתה.`
            }
            confirmText="הפעלה מחדש"
            onConfirm={handleReactivate}
            onClose={() => setConfirmReactivate(false)}
          />
        </div>
      )}
      {/* Nested modals: stop their backdrop clicks from bubbling up to this
          overlay's onClose — each nested modal's own backdrop closes only itself. */}
      {showDeactivate && (
        <div onClick={(e) => e.stopPropagation()}>
          <DeactivateMemberModal
            therapist={therapist}
            onClose={() => setShowDeactivate(false)}
            onDone={onClose}
          />
        </div>
      )}
      {transfer && (
        <div onClick={(e) => e.stopPropagation()}>
          <TransferPatientsModal
            fromTherapist={therapist}
            patientIds={transfer.patientIds}
            onClose={() => setTransfer(null)}
            onDone={() => setSelectedPatients(new Set())}
          />
        </div>
      )}
    </div>
  );
}

export default TeamMemberPopup;
